<template>
  <div class="freelancers content-wrapper content-wrapper--align-start">
    <h1>Freelancers</h1>
    <template v-if="isFetching">
      <div class="table-options">
        <div class="placeholder-search"></div>
      </div>
      <div class="placeholder-table">
        <div class="placeholder-table__header"></div>
      </div>
    </template>
    <template v-else>
      <div class="table-options">
        <span>Filters:</span>
        <div class="select-container select-container--no-label">
          <select v-model="marketFilter">
            <option value="">All Markets</option>
            <option v-for="market in oldMarketList" v-bind:key="market.id" :value="market.id">{{ market.name }}</option>
          </select>
          <!-- prettier-ignore -->
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#999"></path></svg>
        </div>
        <div class="select-container select-container--no-label">
          <select v-model="suspendedFilter">
            <option v-bind:value="true">Suspended</option>
            <option v-bind:value="false">Active</option>
          </select>
          <!-- prettier-ignore -->
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#999"></path></svg>
        </div>
        <div class="search-container">
          <!-- prettier-ignore -->
          <svg data-v-8cd888b6 xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20"><path fill="#bcbccb" data-v-8cd888b6 d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
          <input class="input input--search-icon" v-model="searchFilter" placeholder="Search name" />
        </div>
      </div>

      <div class="table">
        <div class="table__headers">
          <div class="table__header">Name</div>
          <div class="table__header">Languages</div>
          <div class="table__header">Rate per Word</div>
          <div class="table__header">Rate per Review</div>
          <div class="table__header">Overall Rating</div>
          <div class="table__header">{{ fetchDate | moment('MMMM') }} Word Count</div>
          <div class="table__header">{{ fetchDate | moment('MMMM') }} Fee</div>
          <div class="table__header"></div>
        </div>
        <template v-for="user in filteredUserList">
          <div
            v-bind:key="user.id"
            class="table__row"
            @click="
              toggleOpen($event)
              fetchWordCount(user.id, user.content.rates.word)
            "
            ref="rows"
          >
            <div class="table__cell">{{ user.content.name }}</div>
            <div class="table__cell">{{ getLanguageName(user.content.languages) }}</div>
            <div class="table__cell">{{ user.content.rates.word }} {{ user.content.rates.currency }}</div>
            <div class="table__cell">{{ user.content.rates.review }} {{ user.content.rates.currency }}</div>
            <div class="table__cell">{{ overallRating(user.content.rating) }}</div>
            <div v-if="user.content.lastMonth" class="table__cell" :class="{ 'loading-text': isFetchingWordCount }">{{ user.content.lastMonth.wordCount }}</div>
            <div v-if="user.content.lastMonth" class="table__cell" :class="{ 'loading-text': isFetchingWordCount }">{{ user.content.lastMonth.fee }}</div>
            <!-- prettier-ignore -->
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#999"></path></svg>
          </div>
          <div v-bind:key="`${user.id}-foldout`" class="table__foldout">
            <div class="wordcount">
              <div v-if="isFetchingWordCount">Fetching freelancer word count, please wait..</div>
              <div v-else>
                <div v-if="userWordCount === null">No word count</div>
                <div v-else>
                  <table class="inline-table">
                    <tr>
                      <th>Month</th>
                      <th>Words</th>
                      <th>Fee</th>
                    </tr>
                    <tr v-for="item in userWordCount" v-bind:key="item.date">
                      <td>{{ item.date }}</td>
                      <td>{{ item.wordCount }}</td>
                      <td>{{ item.rate }} {{ user.content.rates.currency }}</td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>

            <div class="input-container">
              <label class="input__label">Email</label>
              <input class="input input--readonly" readonly :value="user.content.email" />
            </div>

            <div class="add-language">
              <div class="select-container">
                <label class="input__label">Select language</label>
                <select v-model="user.content.languages">
                  <option value="">None</option>
                  <option v-for="language in languageList" :key="language.id" :value="language.id">{{ language.name }}</option>
                </select>
                <!-- prettier-ignore -->
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#999"></path></svg>
              </div>
            </div>

            <div class="select-container select-container--grid">
              <label class="input__label input__label--full-width">Select secondary languages</label>
              <div v-for="language in languageList" :key="language.id" class="checkbox__container">
                <input :disabled="!isAdmin" type="checkbox" id="language" :value="language.id" v-model="user.content.secondaryLanguages" />
                <label class="checkbox__label" for="language">{{ language.name }}</label>
              </div>
            </div>

            <div class="select-container select-container--grid">
              <label class="input__label input__label--full-width">Select freelancer task types</label>
              <div v-for="type in typeList" :key="type.id" class="checkbox__container">
                <input :disabled="!isAdmin" type="checkbox" id="type" :value="type.id" v-model="user.content.taskTypes" />
                <label class="checkbox__label" for="type">{{ type.name }}</label>
              </div>
            </div>

            <div class="ratings">
              <div class="select-container">
                <label class="input__label">Quality</label>
                <select v-model="user.content.rating.quality">
                  <option v-for="rating in ratingList" v-bind:key="rating" :value="rating">{{ rating }}</option>
                  <option value="0">Unknown</option>
                </select>
                <!-- prettier-ignore -->
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#999"></path></svg>
              </div>
              <div class="select-container">
                <label class="input__label">Availability</label>
                <select v-model="user.content.rating.availability">
                  <option v-for="rating in ratingList" v-bind:key="rating" :value="rating">{{ rating }}</option>
                  <option value="0">Unknown</option>
                </select>
                <!-- prettier-ignore -->
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#999"></path></svg>
              </div>
              <div class="select-container">
                <label class="input__label">Deadlines</label>
                <select v-model="user.content.rating.deadlines">
                  <option v-for="rating in ratingList" v-bind:key="rating" :value="rating">{{ rating }}</option>
                  <option value="0">Unknown</option>
                </select>
                <!-- prettier-ignore -->
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#999"></path></svg>
              </div>
              <div class="select-container">
                <label class="input__label">Cooperation</label>
                <select v-model="user.content.rating.cooperation">
                  <option v-for="rating in ratingList" v-bind:key="rating" :value="rating">{{ rating }}</option>
                  <option value="0">Unknown</option>
                </select>
                <!-- prettier-ignore -->
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#999"></path></svg>
              </div>
            </div>

            <div class="rates">
              <div class="select-container">
                <label class="input__label">Currency</label>
                <select v-model="user.content.rates.currency">
                  <option v-for="currency in currencies" v-bind:key="currency.code" :value="currency.code">{{ currency.name }} ({{ currency.code }})</option>
                </select>
                <!-- prettier-ignore -->
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#999"></path></svg>
              </div>
              <div class="input-container">
                <label class="input__label">Rate per word</label>
                <input class="input" type="number" step="any" v-model="user.content.rates.word" />
              </div>
              <div class="input-container">
                <label class="input__label">Rate per review</label>
                <input class="input" type="number" step="any" v-model="user.content.rates.review" />
              </div>
            </div>

            <div class="address">
              <div class="input-container">
                <label class="input__label">Street address</label>
                <input class="input" type="text" step="any" v-model="user.content.address.street" />
              </div>
              <div class="input-container">
                <label class="input__label">Postal code</label>
                <input class="input" type="text" step="any" v-model="user.content.address.postalCode" />
              </div>
              <div class="input-container">
                <label class="input__label">City</label>
                <input class="input" type="text" step="any" v-model="user.content.address.city" />
              </div>
              <div class="input-container">
                <label class="input__label">Country</label>
                <input class="input" type="text" step="any" v-model="user.content.address.country" />
              </div>
            </div>

            <div class="invoice">
              <div class="input-container">
                <label class="input__label">Bank name</label>
                <input class="input" type="text" step="any" v-model="user.content.invoice.bankName" />
              </div>
              <div class="input-container">
                <label class="input__label">SSN</label>
                <input class="input" type="text" step="any" v-model="user.content.invoice.ssn" />
              </div>
              <div class="input-container">
                <label class="input__label">SWIFT</label>
                <input class="input" type="text" step="any" v-model="user.content.invoice.swift" />
              </div>
              <div class="input-container">
                <label class="input__label">IBAN</label>
                <input class="input" type="text" step="any" v-model="user.content.invoice.iban" />
              </div>
            </div>

            <div class="textarea-container">
              <label class="textarea__label">Comment</label>
              <textarea class="textarea" rows="10" v-model="user.content.comment"></textarea>
            </div>

            <div class="button-container">
              <button
                v-if="user.content.suspended"
                class="button button--thin"
                @click="
                  user.content.suspended = false
                  saveUser(user)
                "
              >
                Unsuspend user
              </button>
              <button
                v-else
                class="button button--thin button--danger"
                @click="
                  user.content.suspended = true
                  saveUser(user)
                "
              >
                Suspend user
              </button>
              <button class="button button--thin button--margin-left" @click="saveUser(user)">
                Save user
              </button>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import firebase from 'firebase'
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  name: 'freelancers',
  data() {
    return {
      isFetching: false,
      ratingList: [1, 2, 3, 4, 5],
      searchFilter: '',
      suspendedFilter: false,
      marketFilter: '',
      selectedLanguage: {},
      currencies: [
        { code: 'kr', name: 'Kroner' },
        { code: '$', name: 'US Dollars' },
        { code: '€', name: 'Euros' },
      ],
      isFetchingWordCount: false,
      userWordCount: null,
      fetchDate: this.$moment().subtract(1, 'month'),
    }
  },
  methods: {
    saveUser(user) {
      console.log(user)

      var content = JSON.parse(JSON.stringify(user.content))
      delete content.lastMonth

      var _this = this
      firebase
        .firestore()
        .collection('users')
        .doc(user.id)
        .set(content)
        .then(function(docRef) {
          console.log('Document successfully written!', docRef)
          _this.$notify({
            text: 'Successfully saved profile',
            type: 'success',
          })
        })
        .catch(function(error) {
          console.error('Error adding document: ', error)
          _this.$notify({
            text: 'Failed to save profile',
            type: 'error',
          })
        })
    },
    getLanguageName(id) {
      return this.$store.getters.languageNameByLanguageID(id)
    },
    fetchWordCount(userId, wordRate) {
      this.isFetchingWordCount = true
      this.userWordCount = null
      var _this = this
      var url = new URL('https://europe-west1-freelance-ffddb.cloudfunctions.net/freelancer-word-count')
      url.searchParams.set('id', userId)
      url.searchParams.set('wordrate', wordRate)
      axios
        .get(url)
        .then(response => {
          _this.userWordCount = response.data
        })
        .catch(function(error) {
          console.log(error)
        })
        .then(function() {
          _this.isFetchingWordCount = false
        })
    },
    toggleOpen($event) {
      this.closeRows($event)
      $event.target.classList.toggle('open')
    },
    closeRows(event) {
      this.$refs.rows.forEach(row => {
        if (!event.target.classList.contains('open')) {
          if (row.classList.contains('open')) {
            row.classList.remove('open')
          }
        }
      })
    },
    overallRating(rating) {
      return (rating.quality + rating.availability + rating.deadlines + rating.cooperation) / 4
    },
    fetchFreelancerWordCount() {
      var _this = this
      var url = new URL('https://europe-west1-freelance-ffddb.cloudfunctions.net/freelancer-word-count')
      url.searchParams.set('start', this.fetchDate.format('YYYYMM'))
      this.isFetchingWordCount = true
      axios
        .get(url)
        .then(response => {
          this.isFetchingWordCount = false
          response.data.forEach(resp => {
            this.$store.getters.users.forEach(user => {
              if (user.id === resp.userId) {
                user.content.lastMonth.wordCount = resp.wordCount
                if (resp.rate > 0) {
                  user.content.lastMonth.fee = resp.currency + ' ' + resp.rate
                }
              }
            })
          })
        })
        .catch(function(error) {
          console.log(error)
        })
    },
  },
  computed: {
    ...mapGetters(['monthList', 'oldMarketList', 'languageList', 'isAdmin', 'users', 'typeList']),
    filteredUserList() {
      return this.users.filter(
        user =>
          !user.content.isAdmin &&
          user.content.suspended === this.suspendedFilter &&
          (this.marketFilter == '' || user.content.languages.some(language => language.id === this.marketFilter)) &&
          (this.searchFilter === '' || user.content.name.toLowerCase().includes(this.searchFilter.toLowerCase()))
      )
    },
  },
  watch: {
    '$store.getters.users': function() {
      if (this.$store.getters.users.length > 0) {
        this.fetchFreelancerWordCount()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/shared-styles/button.scss';
@import '@/assets/shared-styles/input.scss';
@import '@/assets/shared-styles/textarea.scss';
@import '@/assets/shared-styles/select.scss';
@import '@/assets/shared-styles/table.scss';
@import '@/assets/shared-styles/inline-table.scss';

.table__headers,
.table__row {
  grid-template-columns: 180px minmax(100px, 1fr) 1fr 1fr 1fr 1fr 1fr 30px;
}

.ratings,
.rates,
.address,
.invoice {
  display: flex;
}

.address .input-container,
.invoice .input-container,
.rates .input-container,
.rates .select-container,
.ratings .select-container {
  margin-right: 1.5rem;
}

.add-language {
  display: flex;
  align-items: flex-end;
}
.add-language .select-container {
  margin-right: 1rem;
}

.language-list ul {
  list-style: none;
  margin: 0;
  padding: 0;
}
.language-list button {
  border: inherit;
  background-color: inherit;
  cursor: pointer;
  padding: 0;
  color: #c77072;
}

.content-wrapper--align-start {
  grid-template-rows: auto;
  align-content: start;
}
.placeholder-search {
  margin-left: auto;
  background-color: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  width: 240px;
  height: 33px;
}

.select-container--grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
}

.input__label--full-width {
  grid-column: 1 / -1;
  margin-bottom: 0.5rem;
}

.search-container {
  width: 200px;
  margin-left: auto;
  position: relative;
}
.search-container > svg {
  position: absolute;
  transform: translateY(-50%);
  top: 45%;
  left: 0.5rem;
}
.button-container {
  display: flex;
  margin-left: auto;
  margin-top: 1rem;
}

.loading-text {
  height: 16px;
  margin: 0.5rem;
  padding: 0;
  width: 80px;
  animation: pulse 1s infinite ease-in-out;
}

@keyframes pulse {
  0% {
    background-color: rgba(165, 165, 165, 0.1);
  }
  50% {
    background-color: rgba(165, 165, 165, 0.3);
  }
  100% {
    background-color: rgba(165, 165, 165, 0.1);
  }
}
</style>
